import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Zap, Timer, Package, Coins, ArrowRight } from "lucide-react";

// Animated scene per benefit — pure CSS, no assets.
function SceneHit() {
  return (
    <div className="relative h-36 rounded-2xl overflow-hidden bg-black/40 border border-white/[0.06] flex items-center justify-center">
      <div className="absolute inset-0" style={{ background: "radial-gradient(60% 80% at 50% 100%, rgba(10,132,255,0.22), transparent 70%)" }} />
      <div className="absolute bottom-5 flex items-end gap-1.5 h-16">
        {[0, 1, 2, 3, 4].map((i) => (
          <span key={i} className="anim-bar-rise w-2.5 rounded-full bg-gradient-to-t from-[#0A84FF]/40 to-[#0A84FF]"
            style={{ height: `${28 + i * 14}%`, animationDelay: `${i * 0.28}s` }} />
        ))}
      </div>
      <div className="anim-sheen relative text-5xl font-black tracking-tight bg-gradient-to-r from-white via-[#0A84FF] to-white bg-clip-text text-transparent">
        100x
      </div>
    </div>
  );
}

function SceneWindow() {
  return (
    <div className="relative h-36 rounded-2xl overflow-hidden bg-black/40 border border-white/[0.06] flex items-center justify-center">
      <div className="absolute inset-0" style={{ background: "radial-gradient(60% 80% at 50% 100%, rgba(168,85,247,0.20), transparent 70%)" }} />
      <div className="relative w-24 h-24 rounded-full border-2 border-white/15">
        <div className="anim-dial-spin absolute inset-0">
          <span className="absolute left-1/2 top-[6px] -ml-[2px] w-1 h-8 rounded-full bg-[#a855f7]" />
        </div>
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white" />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 translate-y-2 text-[13px] font-bold text-white/85">7d</span>
      </div>
    </div>
  );
}

function SceneOpen() {
  return (
    <div className="relative h-36 rounded-2xl overflow-hidden bg-black/40 border border-white/[0.06] flex items-center justify-center">
      <div className="anim-glow-pulse absolute w-28 h-28 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(52,211,153,0.35), transparent)" }} />
      <div className="anim-crate-loop relative w-16 h-16 rounded-2xl border border-emerald-300/40 flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, rgba(52,211,153,0.25), transparent 60%)", boxShadow: "inset 0 0 24px rgba(52,211,153,0.25)" }}>
        <Package className="w-8 h-8 text-emerald-300" />
      </div>
    </div>
  );
}

function SceneTips() {
  return (
    <div className="relative h-36 rounded-2xl overflow-hidden bg-black/40 border border-white/[0.06] flex items-start justify-center">
      <div className="absolute inset-0" style={{ background: "radial-gradient(60% 80% at 50% 0%, rgba(255,209,102,0.16), transparent 70%)" }} />
      <div className="relative mt-4 flex gap-5">
        {[0, 1, 2].map((i) => (
          <Coins key={i} className="anim-coin-drop w-6 h-6 text-[#FFD166]" style={{ animationDelay: `${i * 0.5}s` }} />
        ))}
      </div>
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 w-32 h-[3px] rounded-full bg-white/15" />
    </div>
  );
}

const BENEFITS = [
  { n: "01", icon: Zap, scene: <SceneHit />, title: "Hit 100x+", text: "Any bet on a Gumble game paying 100x or more earns you a crate. Bigger hits, better odds." },
  { n: "02", icon: Timer, scene: <SceneWindow />, title: "7-day window", text: "Only bets placed within the last 7 days are eligible. Fresh wins only — no archives." },
  { n: "03", icon: Package, scene: <SceneOpen />, title: "Open anytime", text: "Every crate lives on your profile. Open when you're ready — the reveal waits for you." },
  { n: "04", icon: Coins, scene: <SceneTips />, title: "Stake tips inside", text: "Rewards scale with your win. Higher multipliers and bigger bets tip better odds." },
];

export default function CratesSection() {
  const { user, login } = useAuth();

  return (
    <section id="crates" className="relative px-6 md:px-10 py-32">
      <div className="max-w-6xl mx-auto">
        <div className="text-center">
          <div className="text-[12px] uppercase tracking-[0.25em] text-white/40">The crates</div>
          <h2 className="mt-4 text-[52px] md:text-[72px] font-black tracking-[-0.04em] leading-[0.95]">
            Cross 100x.<br /><span className="bg-gradient-to-r from-[#0A84FF] to-[#a855f7] bg-clip-text text-transparent">Earn a crate.</span>
          </h2>
          <p className="mt-5 text-white/60 text-[15px] max-w-xl mx-auto">
            Play on Stake &middot; submit your bet ID &middot; open your crate. Up to 3 crates every 24 hours.
          </p>
          <div className="mt-8">
            {user ? (
              <Link to="/challenges" className="inline-flex items-center gap-2 pl-6 pr-5 py-3 rounded-full bg-white text-black text-[14px] font-semibold hover:bg-white/90">
                Go to challenges <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <button onClick={login} className="inline-flex items-center gap-2 pl-6 pr-5 py-3 rounded-full bg-white text-black text-[14px] font-semibold hover:bg-white/90">
                Sign in with Discord <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map((b) => (
            <div key={b.n} className="rounded-[24px] border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl">
              {b.scene}
              <div className="px-2 pt-4 pb-2">
                <div className="flex items-center gap-2.5">
                  <span className="text-[11px] font-mono text-white/35">{b.n}</span>
                  <b.icon className="w-4 h-4 text-white/70" />
                  <div className="text-[15px] font-semibold text-white">{b.title}</div>
                </div>
                <div className="mt-1.5 text-[13.5px] text-white/60 leading-relaxed">{b.text}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
